import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { globalSearch } from '../api/client';
import { useDebounce } from '../hooks/useDebounce';
import type { RecentItem } from '../hooks/useRecentItems';
import { useCurrentUser } from '../hooks/useCurrentUser';
import Dialog from './Dialog';
import { EVIDENCE_ESCALATIONS_ROLES } from '../constants/roles';
import {
  LayoutDashboard, Kanban, FolderOpen, Users, CalendarDays,
  Bot, FileText, FileWarning, BookOpen, BarChart3, Settings, DollarSign,
  Search, FolderPlus, UserPlus, Zap, Shuffle,
} from 'lucide-react';

interface Props {
  onClose: () => void;
  recentItems: RecentItem[];
  onCreateMatter: () => void;
  onCreateContact: () => void;
  onQuickIntake: () => void;
}

const PAGES = [
  { label: 'Dashboard', path: '/', icon: LayoutDashboard, keywords: ['home', 'overview'] },
  { label: 'Pipeline', path: '/pipeline', icon: Kanban, keywords: ['kanban', 'board', 'stages'] },
  { label: 'AI Tasks', path: '/ai-hub', icon: Bot, keywords: ['tasks', 'queue', 'work', 'todo', 'ai', 'chat', 'ai hub'] },
  { label: 'Matters', path: '/matters', icon: FolderOpen, keywords: ['cases', 'list'] },
  { label: 'Contacts', path: '/contacts', icon: Users, keywords: ['clients', 'people'] },
  { label: 'Calendar', path: '/calendar', icon: CalendarDays, keywords: ['schedule', 'events'] },
  { label: 'Documents', path: '/documents', icon: FileText, keywords: ['files', 'templates'] },
  { label: 'Evidence Escalations', path: '/documents?tab=evidence-escalations', icon: FileWarning, keywords: ['evidence', 'escalations', 'documents', 'requests'], roles: EVIDENCE_ESCALATIONS_ROLES },
  { label: 'AI Billing Review', path: '/#moneta', icon: DollarSign, keywords: ['billing', 'time', 'review', 'moneta'], requiresMoneta: true },
  { label: 'Law Library', path: '/law-library', icon: BookOpen, keywords: ['case law', 'legal research', 'authority', 'research'], roles: ['admin', 'attorney', 'paralegal'] },
  { label: 'Staff Portal', path: '/staff-portal', icon: BookOpen, keywords: ['kb', 'training'] },
  { label: 'Employee Manual', path: '/employee-manual', icon: BookOpen, keywords: ['handbook', 'policy', 'hr', 'pto', 'vacation', 'sick'] },
  { label: 'My KPIs', path: '/staff-portal/kpis', icon: BarChart3, keywords: ['kpi', 'scorecard', 'compliance'], roles: ['admin', 'paralegal'] },
  { label: 'KPIs', path: '/admin/kpis', icon: BarChart3, keywords: ['kpi', 'compliance', 'scorecard'], adminOnly: true },
  { label: 'Reports', path: '/reports', icon: BarChart3, keywords: ['analytics', 'stats'], adminOnly: true },
  { label: 'Intake Setup', path: '/admin/intake-setup', icon: Shuffle, keywords: ['round robin', 'assignment', 'workflow'], adminOnly: true },
  { label: 'Search', path: '/search', icon: Search, keywords: ['find', 'search', 'lookup'] },
  { label: 'Admin', path: '/admin', icon: Settings, keywords: ['settings', 'config'], adminOnly: true },
];

export default function CommandPalette({ onClose, recentItems, onCreateMatter, onCreateContact, onQuickIntake }: Props) {
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();
  const currentUserRole = currentUser?.role;
  const monetaEnabled = Boolean(currentUser?.moneta_enabled);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const hasArrowNavigated = useRef(false);
  const debouncedQuery = useDebounce(query, 200);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleQueryChange = (value: string) => {
    hasArrowNavigated.current = false;
    setSelectedIndex(0);
    setQuery(value);
  };

  // Search API results
  const { data: searchResults } = useQuery({
    queryKey: ['command-palette-search', debouncedQuery],
    queryFn: () => globalSearch(debouncedQuery),
    enabled: debouncedQuery.length >= 2,
  });

  // Build flat list of results
  const allItems = useMemo(() => {
    const items: { type: string; label: string; secondary?: string; path?: string; action?: () => void; icon?: React.ElementType }[] = [];
    const q = query.toLowerCase();
    const isAdmin = currentUserRole === 'admin';

    // Pages
    const filteredPages = q
      ? PAGES.filter((p) => {
          const roleAllowed = !p.roles || (currentUserRole ? p.roles.includes(currentUserRole) : false);
          return roleAllowed
            && (!p.adminOnly || isAdmin)
            && (!p.requiresMoneta || monetaEnabled)
            && (p.label.toLowerCase().includes(q) || p.keywords.some(k => k.includes(q)));
        })
      : [];
    if (filteredPages.length > 0) {
      items.push(...filteredPages.map(p => ({ type: 'Pages', label: p.label, path: p.path, icon: p.icon })));
    }

    // Recent Matters (show when no query or when matching)
    const filteredRecent = q
      ? recentItems.filter(r => r.title.toLowerCase().includes(q) || `m-${r.id}`.includes(q))
      : recentItems.slice(0, 5);
    if (filteredRecent.length > 0) {
      items.push(...filteredRecent.map(r => ({
        type: 'Recent Matters',
        label: r.title,
        secondary: `M-${r.id}${r.stageName ? ` · ${r.stageName}` : ''}`,
        path: `/matters/${r.id}`,
      })));
    }

    // Actions
    const actions = [
      { label: 'New Matter', action: onCreateMatter, icon: FolderPlus, keywords: ['create', 'case'] },
      { label: 'New Contact', action: onCreateContact, icon: UserPlus, keywords: ['create', 'client'] },
      { label: 'Quick Intake', action: onQuickIntake, icon: Zap, keywords: ['create', 'intake'] },
    ];
    const filteredActions = q
      ? actions.filter(a => a.label.toLowerCase().includes(q) || a.keywords.some(k => k.includes(q)))
      : actions;
    if (filteredActions.length > 0) {
      items.push(...filteredActions.map(a => ({ type: 'Actions', label: a.label, action: a.action, icon: a.icon })));
    }

    // Search results from API
    if (searchResults) {
      if (searchResults.matters?.length) {
        items.push(...searchResults.matters.slice(0, 5).map(m => ({
          type: 'Search Results',
          label: m.title,
          secondary: [m.contact_name, m.stage_name].filter(Boolean).join(' · '),
          path: `/matters/${m.id}`,
        })));
      }
      if (searchResults.contacts?.length) {
        items.push(...searchResults.contacts.slice(0, 3).map(c => ({
          type: 'Search Results',
          label: `${c.first_name} ${c.last_name}`,
          secondary: c.email || c.phone || undefined,
          path: `/contacts/${c.id}`,
        })));
      }
    }

    return items;
  }, [query, recentItems, searchResults, onCreateMatter, onCreateContact, onQuickIntake, currentUserRole, monetaEnabled]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      hasArrowNavigated.current = true;
      setSelectedIndex(i => Math.min(i + 1, allItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      hasArrowNavigated.current = true;
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (hasArrowNavigated.current && allItems[selectedIndex]) {
        selectItem(allItems[selectedIndex]);
      } else if (query.trim().length >= 2) {
        // No arrow selection — go to full search page
        navigate(`/search?q=${encodeURIComponent(query.trim())}`);
        onClose();
      }
    }
  };

  // Scroll selected item into view
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector(`[data-index="${selectedIndex}"]`);
    if (el) (el as HTMLElement).scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const selectItem = (item: (typeof allItems)[number]) => {
    if (item.path) {
      navigate(item.path);
      onClose();
    } else if (item.action) {
      item.action();
    }
  };

  // Group items by type for section headers
  let lastType = '';

  return (
    <Dialog
      onClose={onClose}
      ariaLabel="Command palette"
      overlayStyle={{
        alignItems: 'flex-start',
        paddingTop: '15vh',
      }}
      contentStyle={{
          background: 'var(--card)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-lg)',
          border: '1px solid var(--border)',
          width: 'min(520px, 92vw)',
          maxHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          alignSelf: 'flex-start',
        }}
    >
        {/* Search input */}
        <div style={{ padding: '0.75rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Search size={18} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search pages, matters, actions..."
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{
              flex: 1,
              border: 'none',
              background: 'transparent',
              fontSize: '0.95rem',
              color: 'var(--text)',
              outline: 'none',
            }}
          />
          <kbd style={{
            fontSize: '0.6rem',
            padding: '0.1rem 0.35rem',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
            color: 'var(--text-muted)',
            background: 'var(--bg)',
          }}>
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} style={{ overflowY: 'auto', maxHeight: 'calc(60vh - 52px)' }}>
          {allItems.length === 0 && query.length >= 2 && (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              No quick results — press <kbd style={{ padding: '0.1rem 0.3rem', border: '1px solid var(--border)', borderRadius: 3, fontSize: '0.75rem' }}>Enter</kbd> to search everything
            </div>
          )}
          {allItems.length === 0 && query.length < 2 && (
            <div style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.82rem', textAlign: 'center' }}>
              Type to search...
            </div>
          )}
          {allItems.map((item, idx) => {
            const showHeader = item.type !== lastType;
            lastType = item.type;
            const Icon = item.icon;
            return (
              <div key={`${item.type}-${item.label}-${idx}`}>
                {showHeader && (
                  <div style={{
                    padding: '0.5rem 0.75rem 0.25rem',
                    fontSize: '0.65rem',
                    color: 'var(--text-muted)',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}>
                    {item.type}
                  </div>
                )}
                <div
                  data-index={idx}
                  onClick={() => selectItem(item)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    background: idx === selectedIndex ? 'var(--primary-light)' : 'transparent',
                    transition: 'background 0.08s ease',
                  }}
                  onMouseEnter={() => setSelectedIndex(idx)}
                >
                  {Icon && <Icon size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: '0.85rem', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.label}
                    </div>
                    {item.secondary && (
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                        {item.secondary}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {query.length >= 2 && allItems.length > 0 && (
            <div style={{
              padding: '0.5rem 0.75rem', borderTop: '1px solid var(--border)',
              fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'center',
            }}>
              Press <kbd style={{ padding: '0.05rem 0.25rem', border: '1px solid var(--border)', borderRadius: 3, fontSize: '0.68rem' }}>Enter</kbd> without selecting for full search
            </div>
          )}
        </div>
    </Dialog>
  );
}
